/**
 * +castux: the local player's ground cues, drawn as floor decals in the same way as the
 * telegraphs (areas.ts: a ground plane with polygonOffset, shaded in the fragment shader), but in
 * the player's own colours so they never read as enemy danger:
 *  - the ground-spell placement circle (RenderFrame.placement): a thin crisp ring with a soft
 *    inner fill and a small centre mark; spring green-gold when valid, red when out of range or
 *    off the floor. Enemy telegraphs are orange fills with dashed edges.
 *  - the GCD / cast ring at the hero's feet (RenderFrame.castRing): a hairline progress arc that
 *    starts at the top of the screen and runs clockwise, with a faint track, a brighter tint inside
 *    the spell-queue window, and a short "ready" pulse when it ends.
 * Two meshes, two materials, uniforms only per frame (no allocation).
 */
import * as THREE from 'three';
import type { RenderFrame } from '../types';
import { groundY } from './ground';

/** The 2×2 plane is laid flat (rotateX −90°: local (x, y) → (x, 0, −y)) and scaled by uScale; vLocal is in yards, sim axes. */
const VERT = `uniform float uScale; varying vec2 vLocal; void main(){ vLocal = vec2(position.x, -position.z) * uScale; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

/** Placement: uR = radius; ring ~0.13 yd at the edge, soft fill rising toward it, centre dot. */
const PLACE_FRAG = `
uniform float uR; uniform vec3 uColor; uniform float uTime; uniform float uAlpha;
varying vec2 vLocal;
void main(){
  float d = length(vLocal);
  if (d > uR + 0.08) discard;
  float edgeD = abs(uR - d);
  float ring = 1.0 - smoothstep(0.05, 0.11, edgeD);
  float inner = smoothstep(0.0, uR, d);
  float fill = (0.07 + 0.13 * inner * inner) * step(d, uR);
  float breathe = 0.85 + 0.15 * sin(uTime * 4.0);
  float dot = 1.0 - smoothstep(0.1, 0.16, d);
  float a = max(ring * 0.95 * breathe, fill) + dot * 0.6;
  vec3 col = mix(uColor * 0.85, min(uColor * 1.25 + 0.15, vec3(1.0)), ring);
  gl_FragColor = vec4(col, clamp(a * uAlpha, 0.0, 1.0));
}`;

/** Cast ring: uR outer radius, hairline band; progress arc clockwise from uStart (radians, local frame). */
const RING_FRAG = `
uniform float uR; uniform vec3 uColor; uniform vec3 uQueueColor; uniform float uProgress; uniform float uActive;
uniform float uQueue; uniform float uPulse; uniform float uStart; uniform float uAlpha;
varying vec2 vLocal;
const float TAU = 6.28318530718;
void main(){
  float d = length(vLocal);
  float pr = uR * (1.0 + 0.18 * (1.0 - uPulse) * step(0.001, uPulse));
  float band = 1.0 - smoothstep(0.025, 0.06, abs(d - uR));
  float pband = 1.0 - smoothstep(0.03, 0.08, abs(d - pr));
  if (band <= 0.0 && pband <= 0.0) discard;
  // Clockwise (seen from above) angle from the start direction, 0..1.
  float ang = atan(vLocal.y, vLocal.x);
  float t = fract((uStart - ang) / TAU);
  float on = step(t, uProgress) * uActive;
  vec3 col = mix(uColor, uQueueColor, uQueue);
  float a = band * (uActive * 0.16 + on * 0.75);
  a = max(a, pband * uPulse * 0.8);
  gl_FragColor = vec4(col, clamp(a * uAlpha, 0.0, 1.0));
}`;

const VALID = new THREE.Color(0.55, 1.0, 0.5);
const INVALID = new THREE.Color(1.0, 0.24, 0.2);
/** The cast bar's warm gold, slightly cooled, and a whiter tint for the queue window. */
const RING = new THREE.Color(1.0, 0.86, 0.5);
const RING_Q = new THREE.Color(1.0, 0.98, 0.9);

function decalMaterial(frag: string, uniforms: Record<string, THREE.IUniform>): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: frag,
    uniforms,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
}

export class PlayerGroundCues {
  readonly group = new THREE.Group();
  private readonly geo = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  private readonly placeMat: THREE.ShaderMaterial;
  private readonly ringMat: THREE.ShaderMaterial;
  private readonly place: THREE.Mesh;
  private readonly ring: THREE.Mesh;
  private time = 0;

  constructor() {
    this.placeMat = decalMaterial(PLACE_FRAG, { uScale: { value: 1 }, uR: { value: 1 }, uColor: { value: VALID.clone() }, uTime: { value: 0 }, uAlpha: { value: 1 } });
    this.ringMat = decalMaterial(RING_FRAG, {
      uScale: { value: 1 },
      uR: { value: 1 },
      uColor: { value: RING.clone() },
      uQueueColor: { value: RING_Q.clone() },
      uProgress: { value: 0 },
      uActive: { value: 0 },
      uQueue: { value: 0 },
      uPulse: { value: 0 },
      uStart: { value: 0 },
      uAlpha: { value: 1 },
    });
    this.place = new THREE.Mesh(this.geo, this.placeMat);
    this.ring = new THREE.Mesh(this.geo, this.ringMat);
    this.place.renderOrder = 4;
    this.ring.renderOrder = 4;
    this.place.visible = this.ring.visible = false;
    this.place.frustumCulled = this.ring.frustumCulled = false;
    this.group.add(this.place, this.ring);
  }

  /** `camera`: the start of the cast ring's arc faces away from it (the top of the screen). */
  update(frame: RenderFrame, camera: THREE.Camera, dtS: number, fxIntensity: number): void {
    this.time += dtS;
    const k = Math.min(1.2, 0.6 + 0.4 * fxIntensity);
    const p = frame.placement;
    this.place.visible = !!p;
    if (p) {
      const pad = p.radius + 0.15;
      this.place.scale.setScalar(pad);
      this.place.position.set(p.x, groundY(p.x, p.y) + 0.05, -p.y);
      const u = this.placeMat.uniforms;
      u['uScale']!.value = pad;
      u['uR']!.value = p.radius;
      (u['uColor']!.value as THREE.Color).copy(p.valid ? VALID : INVALID);
      u['uTime']!.value = this.time;
      u['uAlpha']!.value = k;
    }
    const r = frame.castRing;
    this.ring.visible = !!r;
    if (r) {
      const R = Math.max(0.7, r.radius * 1.1 + 0.2);
      const pad = R + 0.4;
      this.ring.scale.setScalar(pad);
      this.ring.position.set(r.x, groundY(r.x, r.y) + 0.04, -r.y);
      const u = this.ringMat.uniforms;
      u['uScale']!.value = pad;
      u['uR']!.value = R;
      u['uProgress']!.value = r.progress;
      u['uActive']!.value = r.active ? 1 : 0;
      u['uQueue']!.value = r.queue ? 1 : 0;
      u['uPulse']!.value = r.pulse;
      u['uAlpha']!.value = k;
      // Arc start: the direction from the camera to the hero, i.e. "up" on screen (sim coords).
      const dx = r.x - camera.position.x;
      const dy = r.y + camera.position.z;
      u['uStart']!.value = Math.atan2(dy, dx);
    }
  }

  /** Prewarm: show both decals for the renderer's one-off compile (the next update hides them again). */
  warm(on: boolean): void {
    this.place.visible = this.ring.visible = on;
  }

  dispose(): void {
    this.geo.dispose();
    this.placeMat.dispose();
    this.ringMat.dispose();
  }
}
